import {
  MapContainer as BaseMapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import Leaflet from 'leaflet'
import styled from 'styled-components'
import { ReactNode, useEffect, useRef } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import MarkerClusterGroup from 'react-leaflet-cluster'
import 'leaflet.markercluster'

Leaflet.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})
export interface MarkerProps {
  lat: number
  lon: number
  popup?: ReactNode
  markerItemNumberLabel?: string
  isSelected?: boolean
  onClick?: () => void
}

interface PrivateMarkerProps extends MarkerProps {
  type: 'center' | 'pin'
}
const MapContainer = styled(BaseMapContainer)`
  width: 100%;
  height: 100%;
  z-index: 0;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    height: 100vh;
  }
  /*マーカーのCSS*/
  .center_icon {
    position: absolute;
  }

  .center_icon:before {
    content: '';
    position: absolute;
    width: 28px;
    height: 28px;
    left: -16px;
    top: -30px;
    border: solid 1px;
    background: currentColor;
    border-radius: 14px 14px 14px 0;
    -webkit-transform: rotate(-45deg);
    -ms-transform: rotate(-45deg);
    transform: rotate(-45deg);
    box-sizing: content-box;
  }

  // センターマーカーの中心の丸
  .center_icon:after {
    content: '';
    position: absolute;
    left: -8px;
    top: -21px;
    width: 12px;
    height: 12px;
    background-color: white;
    border-radius: 6px;
  }

  .pin_icon {
    position: absolute;
  }

  .pin_icon:before {
    content: '';
    position: absolute;
    width: 28px;
    height: 28px;
    left: -16px;
    top: -30px;
    border: solid 1px;
    background: currentColor;
    border-radius: 14px 14px 14px 0;
    -webkit-transform: rotate(-45deg);
    -ms-transform: rotate(-45deg);
    transform: rotate(-45deg);
    box-sizing: content-box;
  }

  /*マーカーの色のCSS*/
  .center_color {
    background-color: ${(p) => p.theme.RED};
    color: ${(p) => p.theme.RED};
    border-color: ${(p) => p.theme.RED};
  }

  .pin_color {
    background-color: ${(p) => p.theme.MAIN};
    color: ${(p) => p.theme.MAIN};
    border-color: ${(p) => p.theme.MAIN};
  }

  .pin_selected {
    background-color: ${(p) => p.theme.RED};
    color: ${(p) => p.theme.RED};
    border-color: ${(p) => p.theme.RED};
    transform: scale(1.2);
  }
  .marker-icon {
    color: black;
    position: fixed;
    inset: 0;
    margin: auto;
    top: -30px;
    left: -4px;
  }

  /* クラスターのスタイルを全て緑色に統一 */
  .marker-cluster-small,
  .marker-cluster-medium,
  .marker-cluster-large {
    background-color: rgba(110, 204, 57, 0.6);
  }

  .marker-cluster-small div,
  .marker-cluster-medium div,
  .marker-cluster-large div {
    background-color: rgba(110, 204, 57, 0.8);
  }
`

const MapIcon = styled.div<{ length?: number }>`
  color: black;
  position: fixed;
  inset: 0;
  margin: auto;
  top: -30px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  // 一桁と二桁で位置がずれるので調整、三桁は考慮していない
  left: ${(p) => ((p.length ?? 1) === 1 ? '-1px' : '-2px')};
`
const MapMarker = ({ props }: { props: PrivateMarkerProps }) => {
  const mapIconText =
    props.markerItemNumberLabel !== undefined ? props.markerItemNumberLabel : ''

  const getClassName = () => {
    if (props.type === 'center') {
      return 'center_color center_icon'
    }
    if (props.isSelected) {
      return 'pin_selected pin_icon'
    }
    return 'pin_color pin_icon'
  }

  const icon = Leaflet.divIcon({
    html: renderToStaticMarkup(
      <MapIcon length={mapIconText.length}>{mapIconText}</MapIcon>
    ),
    className: getClassName(),
    iconSize: [0, 0],
  })

  return (
    <Marker
      position={[props.lat, props.lon]}
      icon={icon}
      eventHandlers={{
        click: () => {
          props.onClick?.()
        },
      }}
    >
      {props.popup && <Popup>{props.popup}</Popup>}
    </Marker>
  )
}

interface PositionType {
  lat: number
  lon: number
}
const calcPosition = (
  position: [number, number] | undefined,
  markers: MarkerProps[]
): PositionType | undefined => {
  if (position) {
    return {
      lat: position[0],
      lon: position[1],
    }
  }

  if (markers.length > 0) {
    return { lat: markers[0]!.lat, lon: markers[0]!.lon }
  }

  return undefined
}

const MapController = ({
  center,
  zoom,
  isMobile = false,
  panelHeight = 0,
  clusterGroupRef,
}: {
  center?: [number, number]
  zoom?: number
  isMobile?: boolean
  panelHeight?: number
  clusterGroupRef?: React.RefObject<Leaflet.MarkerClusterGroup>
}) => {
  const map = useMap()

  useEffect(() => {
    if (center && zoom) {
      if (isMobile && panelHeight > 0) {
        // パネル高さの50%分だけアイコンを上にずらす（pxベース）
        const offsetY = -1 * panelHeight * 0.5
        const projectedCenter = map.project(center, zoom)
        const adjustedCenter = map.unproject(
          projectedCenter.subtract([0, offsetY]),
          zoom
        )

        map.setView(adjustedCenter, zoom, { animate: true })
      } else {
        map.setView(center, zoom, { animate: true })
      }

      // クラスターを展開
      if (clusterGroupRef?.current) {
        const timer = setTimeout(() => {
          const cluster = clusterGroupRef.current
          if (cluster) {
            // 選択されたマーカーの位置にあるクラスターを展開
            const layers = cluster.getLayers()
            for (let i = 0; i < layers.length; i++) {
              const layer = layers[i]
              if (layer instanceof Leaflet.Marker) {
                const latLng = layer.getLatLng()
                if (
                  latLng &&
                  Math.abs(latLng.lat - center[0]) < 0.0001 &&
                  Math.abs(latLng.lng - center[1]) < 0.0001
                ) {
                  cluster.zoomToShowLayer(layer, () => {
                    // クラスター展開後にマーカーを表示
                    if (map.getZoom() < zoom) {
                      map.setZoom(zoom)
                    }
                  })
                  break
                }
              }
            }
          }
        }, 300)

        return () => clearTimeout(timer)
      }
    }
    return undefined
  }, [map, center, zoom, isMobile, panelHeight, clusterGroupRef])

  return null
}

const Map = (props: {
  position?: [number, number] | undefined
  markers: MarkerProps[]
  selectedItemId?: string
  selectedCenter?: [number, number]
  selectedZoom?: number
  isMobile?: boolean
  panelHeight?: number
  onMarkerClick?: (markerIndex: number) => void
}) => {
  const position = calcPosition(props.position, props.markers)
  const clusterGroupRef = useRef<Leaflet.MarkerClusterGroup>(null)

  if (!position) {
    return <></>
  }
  return (
    <MapContainer
      center={[position.lat, position.lon]}
      zoom={14}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapController
        center={props.selectedCenter}
        zoom={props.selectedZoom}
        isMobile={props.isMobile}
        panelHeight={props.panelHeight}
        clusterGroupRef={clusterGroupRef}
      />

      {/* センターマーカー */}
      {props.position && (
        <MapMarker
          props={{
            lat: props.position[0],
            lon: props.position[1],
            type: 'center',
          }}
        />
      )}
      {/* propsのmarker */}
      <MarkerClusterGroup
        ref={clusterGroupRef}
        showCoverageOnHover={false}
        spiderfyOnMaxZoom={true}
        maxClusterRadius={(zoom) => {
          // ズームレベルが高い（拡大されている）時はクラスタリングの範囲を狭く
          // ズームレベルが低い（縮小されている）時はクラスタリングの範囲を広く
          if (zoom >= 18) return 1 // 最大ズーム時は同じ位置のマーカーのみグループ化
          if (zoom >= 16) return 20
          if (zoom >= 14) return 40
          if (zoom >= 12) return 60
          return 80 // 縮小時は広範囲でグループ化
        }}
        spiderfyDistanceMultiplier={2}
      >
        {props.markers.map((marker, index) => {
          const privateProps: PrivateMarkerProps = {
            ...marker,
            type: 'pin',
            isSelected: props.selectedItemId === `marker-${index}`,
            onClick: () => {
              props.onMarkerClick?.(index)
            },
          }
          return <MapMarker key={`map-marker-${index}`} props={privateProps} />
        })}
      </MarkerClusterGroup>
    </MapContainer>
  )
}

export default Map
